import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, errorMessage, unwrap } from "../api/client";
import type { components } from "../api/schema";
import {
  Button, Checkbox, ConfirmDialog, Field, FormError, Modal, PageHeader, Panel, Pill, Segmented, Select, TextInput,
  useToast,
} from "../components/ui";
import { AccessPanel } from "../components/AccessPanel";
import { ContactsPanel } from "../components/Contacts";
import { SignupPanel } from "../components/SignupPanel";
import { formatBytes } from "../lib/format";
import { getLang, LANGS, N_, rich, t } from "../lib/i18n";

type Channel = components["schemas"]["ChannelView"];
type ChannelIn = components["schemas"]["ChannelIn"];
type ChannelType = ChannelIn["type"];

const TYPE_LABEL: Record<ChannelType, string> = { webhook: "Webhook", telegram: "Telegram", email: "E-mail" };
const LEVEL_LABEL: Record<ChannelIn["min_level"], string> = {
  warning: N_("Alertes et plus graves"), error: N_("Erreurs et critiques"), critical: N_("Critiques seulement"),
};

function emptyChannel(): ChannelIn {
  return {
    name: "", type: "webhook", enabled: true, min_level: "warning", notify_clear: true, url: "", bot_token: "",
    chat_id: "", smtp_host: "", smtp_port: 587, smtp_security: "starttls", smtp_user: "", smtp_password: "",
    mail_from: "", mail_to: "", lang: "fr",
  };
}

function ChannelForm({ channel, onClose, onSaved }: { channel?: Channel; onClose: () => void; onSaved: () => void }) {
  const [c, setC] = useState<ChannelIn>(() => channel ? { ...emptyChannel(), ...channel, smtp_password: "" } : emptyChannel());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof ChannelIn>(k: K, v: ChannelIn[K]) => setC((p) => ({ ...p, [k]: v }));
  const text = (k: keyof ChannelIn) => ({
    value: String(c[k] ?? ""), onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(k, e.target.value as never),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { ...c, smtp_password: c.smtp_password || null };
    try {
      if (channel) await unwrap(api.PUT("/api/notifications/{channel_id}", { params: { path: { channel_id: channel.id } }, body }));
      else await unwrap(api.POST("/api/notifications", { body }));
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open wide title={channel ? t("Modifier « {name} »", { name: channel.name }) : t("Nouveau canal de notification")} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Annuler")}</Button>
        <Button variant="primary" type="submit" form="channel-form" disabled={busy}>{t("Enregistrer")}</Button>
      </>}>
      <form id="channel-form" onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Nom")} htmlFor="ch-name">
            <TextInput id="ch-name" required autoFocus placeholder={t("Astreinte technique")} {...text("name")} />
          </Field>
          <Field label={t("Type")}>
            <Segmented label={t("Type")} value={c.type} onChange={(v) => set("type", v)}
              options={(["webhook", "telegram", "email"] as const).map((t) => ({ value: t, label: TYPE_LABEL[t] }))} />
          </Field>
        </div>

        {c.type === "webhook" && (
          <Field label={t("URL")} htmlFor="ch-url" hint={t("Reçoit un POST JSON (state, level, message, text…) : Slack, Teams, Discord, n8n, outil maison.")}>
            <TextInput id="ch-url" required placeholder={t("https://hooks.exemple.com/…")} className="font-mono text-[13px]" {...text("url")} />
          </Field>
        )}
        {c.type === "telegram" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("Jeton du bot")} htmlFor="ch-bot" hint={t("Créé avec @BotFather dans Telegram.")}>
              <TextInput id="ch-bot" required className="font-mono text-[13px]" {...text("bot_token")} />
            </Field>
            <Field label={t("Chat ID")} htmlFor="ch-chat" hint={t("Identifiant du groupe ou de la personne (ex. -1001234567890).")}>
              <TextInput id="ch-chat" required className="font-mono text-[13px]" {...text("chat_id")} />
            </Field>
          </div>
        )}
        {c.type === "email" && (
          <>
            <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
              <Field label={t("Serveur SMTP")} htmlFor="ch-host"><TextInput id="ch-host" required placeholder={t("smtp.exemple.com")} {...text("smtp_host")} /></Field>
              <Field label={t("Port")} htmlFor="ch-port">
                <TextInput id="ch-port" type="number" min={1} max={65535} value={c.smtp_port}
                  onChange={(e) => set("smtp_port", Number(e.target.value))} />
              </Field>
              <Field label={t("Sécurité")} htmlFor="ch-sec">
                <Select id="ch-sec" value={c.smtp_security} onChange={(e) => set("smtp_security", e.target.value as ChannelIn["smtp_security"])}>
                  <option value="starttls">STARTTLS (587)</option><option value="ssl">SSL (465)</option><option value="none">{t("Aucune")}</option>
                </Select>
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("Utilisateur SMTP")} htmlFor="ch-user"><TextInput id="ch-user" autoComplete="off" {...text("smtp_user")} /></Field>
              <Field label={t("Mot de passe SMTP")} htmlFor="ch-pass" hint={channel?.has_password ? t("Laisser vide pour garder le mot de passe enregistré.") : undefined}>
                <TextInput id="ch-pass" type="password" autoComplete="new-password" {...text("smtp_password")} />
              </Field>
              <Field label={t("Expéditeur")} htmlFor="ch-from"><TextInput id="ch-from" required type="email" placeholder={t("gateway@exemple.com")} {...text("mail_from")} /></Field>
              <Field label={t("Destinataires")} htmlFor="ch-to" hint={t("Séparés par des virgules.")}>
                <TextInput id="ch-to" required placeholder={t("astreinte@exemple.com, regie@exemple.com")} {...text("mail_to")} />
              </Field>
            </div>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Alarmes envoyées")} htmlFor="ch-level">
            <Select id="ch-level" value={c.min_level} onChange={(e) => set("min_level", e.target.value as ChannelIn["min_level"])}>
              {(["warning", "error", "critical"] as const).map((l) => <option key={l} value={l}>{t(LEVEL_LABEL[l])}</option>)}
            </Select>
          </Field>
          <Field label={t("Langue des messages")} htmlFor="ch-lang">
            <Select id="ch-lang" value={c.lang ?? "fr"} onChange={(e) => set("lang", e.target.value as ChannelIn["lang"])}>
              {LANGS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </Select>
          </Field>
          <div className="flex flex-col justify-end gap-2 pb-1">
            <Checkbox id="ch-clear" label={t("Prévenir aussi à la fin de l'alarme")} checked={c.notify_clear} onChange={(v) => set("notify_clear", v)} />
            <Checkbox id="ch-enabled" label={t("Canal actif")} checked={c.enabled} onChange={(v) => set("enabled", v)} />
          </div>
        </div>
        <FormError message={error} />
      </form>
    </Modal>
  );
}

function NotificationsPanel() {
  const toast = useToast();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [editing, setEditing] = useState<Channel | "new" | null>(null);
  const [deleting, setDeleting] = useState<Channel | null>(null);
  const [testing, setTesting] = useState<number | null>(null);

  const load = useCallback(() => {
    unwrap(api.GET("/api/notifications")).then(setChannels).catch((e: Error) => toast(e.message, "err"));
  }, [toast]);
  useEffect(load, [load]);

  const test = async (c: Channel) => {
    setTesting(c.id);
    try {
      const r = await unwrap(api.POST("/api/notifications/{channel_id}/test", { params: { path: { channel_id: c.id } } }));
      toast(r.ok ? t("Message d'essai envoyé sur « {name} »", { name: c.name }) : t("Échec sur « {name} » : {error}", { name: c.name, error: r.error ?? "" }), r.ok ? "ok" : "err");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setTesting(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await unwrap(api.DELETE("/api/notifications/{channel_id}", { params: { path: { channel_id: deleting.id } } }));
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setDeleting(null);
  };

  return (
    <Panel title={t("Notifications d'alarme")} actions={<Button size="sm" variant="primary" onClick={() => setEditing("new")}>{t("Nouveau canal")}</Button>}>
      {channels.length === 0 ? (
        <p className="p-4 text-muted">
          {t("Aucun canal : les alarmes ne s'affichent que dans l'interface. Ajoutez un e-mail, un groupe Telegram ou un webhook pour être prévenu quand personne ne regarde l'écran.")}
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {channels.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <Pill tone={c.enabled ? "ok" : "muted"}>{c.enabled ? t("Actif") : t("Coupé")}</Pill>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{c.name}</div>
                <div className="truncate text-[12px] text-muted">
                  {TYPE_LABEL[c.type]} · {t(LEVEL_LABEL[c.min_level])}{c.notify_clear ? ` · ${t("fin d'alarme")}` : ""}
                  {c.type === "email" && c.mail_to ? ` · ${c.mail_to}` : ""} · {(c.lang ?? "fr").toUpperCase()}
                </div>
              </div>
              <Button size="sm" onClick={() => test(c)} disabled={testing === c.id}>{testing === c.id ? t("Envoi…") : t("Tester")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>{t("Modifier")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleting(c)}>{t("Supprimer")}</Button>
            </li>
          ))}
        </ul>
      )}
      {editing && <ChannelForm channel={editing === "new" ? undefined : editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); toast(t("Canal enregistré. Pensez à le tester.")); }} />}
      <ConfirmDialog open={deleting !== null} title={t("Supprimer le canal ?")} confirmLabel={t("Supprimer")}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {t("« {name} » ne recevra plus les alarmes.", { name: deleting?.name ?? "" })}
      </ConfirmDialog>
    </Panel>
  );
}

type Storage = components["schemas"]["StorageView"];

function StoragePanel() {
  const toast = useToast();
  const [st, setSt] = useState<Storage | null>(null);
  const [dir, setDir] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    unwrap(api.GET("/api/storage")).then((s) => { setSt(s); setDir(s.record_dir); }).catch((e: Error) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  const apply = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      const s = await unwrap(api.PUT("/api/storage", { body: { record_dir: value } }));
      setSt(s);
      setDir(s.record_dir);
      toast(t("Enregistrements dans {dir}. Les fichiers existants y sont déplacés en arrière-plan (voir le journal).", { dir: s.record_dir }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  if (!st) return <Panel title={t("Enregistrements")}><p className="p-4 text-muted">{error ?? t("Chargement…")}</p></Panel>;
  const usedPct = st.total_bytes ? Math.round(((st.total_bytes - st.free_bytes) / st.total_bytes) * 100) : 0;
  const freeGb = st.free_bytes / 1e9;
  const tone = freeGb < st.critical_gb ? "bg-err" : freeGb < st.warn_gb ? "bg-warn" : "bg-accent";
  const changed = dir.trim() !== st.record_dir;

  return (
    <Panel title={t("Enregistrements")}>
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
            <span className="text-muted">
              {rich("Disque du dossier : <v>{free}</v> libres sur {total} · enregistrements : <v>{used}</v>", { v: (s) => <span className="num text-ink">{s}</span> },
                { free: formatBytes(st.free_bytes), total: formatBytes(st.total_bytes), used: formatBytes(st.recordings_bytes) })}
            </span>
            <span className="text-[12px] text-faint">{t("conservés {hours} h · alerte sous {gb} Go", { hours: st.keep_hours, gb: st.warn_gb })}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-raised" role="meter" aria-label={t("Disque occupé")}
            aria-valuenow={usedPct} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full ${tone}`} style={{ width: `${usedPct}%` }} />
          </div>
        </div>
        <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (changed) setConfirming(dir.trim()); }}>
          <Field label={t("Dossier des enregistrements")} htmlFor="st-dir"
            hint={st.is_default ? t("Dossier par défaut, sur le disque du système. Un disque dédié évite de le remplir.")
              : t("Dossier par défaut : {dir}", { dir: st.default_dir })}>
            <div className="flex flex-wrap gap-2">
              <TextInput id="st-dir" value={dir} onChange={(e) => setDir(e.target.value)} className="min-w-0 flex-1 font-mono text-[13px]"
                placeholder={t("F:\\GatewayLive\\enregistrements")} />
              <Button variant="primary" type="submit" disabled={!changed || busy}>{t("Changer")}</Button>
              {!st.is_default && <Button onClick={() => setConfirming("")} disabled={busy}>{t("Dossier par défaut")}</Button>}
            </div>
          </Field>
          <FormError message={error} />
        </form>
      </div>
      <ConfirmDialog open={confirming !== null} title={t("Changer de dossier ?")} confirmLabel={t("Changer")} busy={busy}
        onCancel={() => setConfirming(null)} onConfirm={() => confirming !== null && apply(confirming)}>
        {rich("Les enregistrements en cours reprendront dans <dir>{dir}</dir> (le fichier en cours est coupé à ce moment-là), puis les {size} déjà enregistrés y seront déplacés. Le dossier est d'abord créé et testé : s'il n'est pas utilisable, rien ne change.",
          { dir: (s) => <span className="font-mono text-ink">{s}</span> }, { dir: confirming || st.default_dir, size: formatBytes(st.recordings_bytes) })}
      </ConfirmDialog>
    </Panel>
  );
}

interface PendingRestore { name: string; data: Record<string, unknown>; inputs: number; outputs: number; users: number | null }

function NdiPanel() {
  const toast = useToast();
  const [st, setSt] = useState<components["schemas"]["NdiStatus"] | null>(null);
  const [discovery, setDiscovery] = useState("");
  const [groups, setGroups] = useState("Public");
  const [machine, setMachine] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    unwrap(api.GET("/api/ndi")).then((s) => { setSt(s); setDiscovery(s.discovery); setGroups(s.groups); setMachine(s.machine_name); })
      .catch((e: Error) => setError(e.message));
  }, []);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await unwrap(api.PUT("/api/ndi", { body: { discovery, groups, machine_name: machine } }));
      setSt(s);
      setDiscovery(s.discovery);
      setGroups(s.groups);
      setMachine(s.machine_name);
      toast(t("Réglages NDI enregistrés"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!st) return <Panel title="NDI"><p className="p-4 text-muted">{error ?? t("Chargement…")}</p></Panel>;
  const changed = discovery !== st.discovery || groups !== st.groups || machine !== st.machine_name;
  return (
    <Panel title="NDI">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap gap-2">
          {st.available ? <Pill tone="ok">{t("Bibliothèque NDI trouvée")}</Pill> : <Pill tone="warn">{t("Bibliothèque NDI absente")}</Pill>}
          {st.discovery_reachable === true && <Pill tone="ok">{t("Discovery Server joignable")}</Pill>}
          {st.discovery_reachable === false && <Pill tone="err">{t("Discovery Server injoignable")}</Pill>}
        </div>
        {!st.available && (
          <p className="text-[13px] text-muted">
            {t("La bibliothèque NDI n'est pas livrée avec la gateway : sa licence demande de la télécharger et de l'accepter sur ce serveur. Voir le guide d'installation. Tant qu'elle est absente, le type de sortie NDI n'est pas proposé.")}
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Discovery Server")} htmlFor="ndi-discovery" hint={t("Adresse, ou plusieurs séparées par des virgules. Vide : découverte automatique sur le réseau local.")}>
            <TextInput id="ndi-discovery" value={discovery} maxLength={400} onChange={(e) => setDiscovery(e.target.value)}
              placeholder="10.0.0.5" className="font-mono text-[13px]" />
          </Field>
          <Field label={t("Groupes NDI")} htmlFor="ndi-groups" hint={t("« Public » sauf consigne contraire de votre réseau.")}>
            <TextInput id="ndi-groups" value={groups} maxLength={120} onChange={(e) => setGroups(e.target.value)} />
          </Field>
          <Field label={t("Nom de la machine")} htmlFor="ndi-machine" hint={t("Affiché devant la source : « NOM (source) ». Vide : le nom du serveur.")}>
            <TextInput id="ndi-machine" value={machine} maxLength={63} onChange={(e) => setMachine(e.target.value)}
              placeholder="GATEWAY" />
          </Field>
        </div>
        <FormError message={error} />
        <div><Button variant="primary" disabled={!changed || busy} onClick={() => setConfirming(true)}>{t("Enregistrer")}</Button></div>
      </div>
      <ConfirmDialog open={confirming} title={t("Changer les réglages NDI ?")} confirmLabel={t("Enregistrer et relancer")} busy={busy}
        onCancel={() => setConfirming(false)} onConfirm={apply}>
        {t("Les sorties NDI seront relancées : quelques secondes de coupure pour elles. Les autres sorties ne sont pas touchées.")}
      </ConfirmDialog>
    </Panel>
  );
}

function BackupPanel() {
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [withUsers, setWithUsers] = useState(false);
  const [pending, setPending] = useState<PendingRestore | null>(null);
  const [restoreUsers, setRestoreUsers] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportConfig = async () => {
    const res = await fetch(`/api/backup?include_users=${withUsers}`,
      { credentials: "same-origin", headers: { "X-Gateway-Lang": getLang() } });
    if (!res.ok) {
      toast(errorMessage(await res.json().catch(() => null), res.status), "err");
      return;
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "gateway-config.json";
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
    toast(t("Configuration exportée. Rangez le fichier en lieu sûr : il contient les clés."));
  };

  const pick = async (f: File | undefined) => {
    if (!f) return;
    try {
      const data = JSON.parse(await f.text()) as Record<string, unknown>;
      if (data.format !== "gateway-live-backup") throw new Error(t("ce fichier n'est pas une sauvegarde Gateway Live"));
      const len = (k: string) => (Array.isArray(data[k]) ? (data[k] as unknown[]).length : 0);
      setRestoreUsers(false);
      setPending({ name: f.name, data, inputs: len("inputs"), outputs: len("outputs"), users: Array.isArray(data.users) ? len("users") : null });
    } catch (e) {
      toast(t("Fichier illisible : {error}", { error: (e as Error).message }), "err");
    } finally {
      if (file.current) file.current.value = "";
    }
  };

  const restore = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const r = await unwrap(api.POST("/api/backup/restore", { params: { query: { restore_users: restoreUsers } }, body: pending.data }));
      toast(t("Configuration restaurée : {inputs} entrées, {outputs} sorties, {channels} canaux.", { inputs: r.inputs, outputs: r.outputs, channels: r.channels }));
      setPending(null);
      if (r.users !== null) window.location.reload();   // comptes remplacés : reconnexion
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={t("Sauvegarde de la configuration")}>
      <div className="grid gap-6 p-4 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <p className="text-muted">{t("Entrées, sorties et canaux de notification dans un fichier JSON, pour restaurer ou cloner un serveur.")}</p>
          <Checkbox id="bk-users" label={t("Inclure les comptes (mots de passe hachés)")} checked={withUsers} onChange={setWithUsers} />
          <div><Button onClick={exportConfig}>{t("Exporter la configuration")}</Button></div>
          <p className="text-[12px] text-warn">{t("Le fichier contient les clés de flux, passphrases et clés de stream : à protéger.")}</p>
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-muted">{t("Remplace toute la configuration actuelle. Les sorties sont arrêtées puis relancées (coupure de quelques secondes).")}</p>
          <input ref={file} type="file" accept="application/json,.json" className="hidden" id="bk-file"
            onChange={(e) => pick(e.target.files?.[0])} />
          <div><Button variant="danger" onClick={() => file.current?.click()}>{t("Restaurer une sauvegarde…")}</Button></div>
        </div>
      </div>
      <ConfirmDialog open={pending !== null} title={t("Restaurer cette sauvegarde ?")} confirmLabel={t("Remplacer la configuration")} busy={busy}
        onCancel={() => setPending(null)} onConfirm={restore}>
        <div className="flex flex-col gap-3">
          <p>{t("« {name} » : {inputs} entrées et {outputs} sorties. La configuration actuelle sera entièrement remplacée et toutes les sorties redémarrées.", { name: pending?.name ?? "", inputs: pending?.inputs ?? 0, outputs: pending?.outputs ?? 0 })}</p>
          {pending?.users !== null && pending?.users !== undefined && (
            <Checkbox id="bk-restore-users" label={t("Remplacer aussi les {n} comptes (tout le monde devra se reconnecter)", { n: pending.users })}
              checked={restoreUsers} onChange={setRestoreUsers} />
          )}
        </div>
      </ConfirmDialog>
    </Panel>
  );
}

function PrometheusPanel() {
  return (
    <Panel title={t("Supervision externe (Prometheus / Grafana)")}>
      <div className="flex flex-col gap-2 p-4 text-muted">
        <p>{t("Les mesures de la gateway sont publiées au format Prometheus :")}</p>
        <code className="w-fit rounded bg-bg px-2 py-1 font-mono text-[13px] text-ink">{window.location.origin}/api/metrics/prometheus</code>
        <p className="text-[13px]">
          {rich("Accès avec le jeton de service (<c>Authorization: Bearer …</c>, réglage <c>bearer_token</c> de Prometheus), ou librement depuis le serveur lui-même. Mesures : signal et débit par entrée, RTT et pertes SRT, état, débit et relances des sorties, sorties sur secours, alarmes actives. ServerMedia publie aussi les siennes sur le port 9998 (local).", { c: (s) => <code className="font-mono">{s}</code> })}
        </p>
      </div>
    </Panel>
  );
}

export function Settings() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Réglages")} subtitle={t("Notifications, inscriptions, contacts, enregistrements, NDI, sauvegarde et supervision externe")} />
      <NotificationsPanel />
      <SignupPanel />
      <ContactsPanel />
      <AccessPanel />
      <StoragePanel />
      <NdiPanel />
      <BackupPanel />
      <PrometheusPanel />
    </div>
  );
}
